import React, { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { MiniKit } from '@worldcoin/minikit-js';
import { UserProfile, userAPI, supabase } from '../lib/supabaseClient';
import { useToast } from '../components/ToastContainer';
import {
  acknowledgeCentralSsoPopupCallback,
  CentralSsoError,
  clearCentralSsoTransaction,
  completeCentralSsoCallbackOnce,
  completeCentralSsoPopupCallback,
  isCentralSsoPopupCallbackMessage,
  startCentralSsoPopup,
} from '../lib/centralSso';
import { signInWithWorldWallet, WorldWalletAuthError } from '../lib/worldWalletAuth';
import { bridgeSupabaseSessionToFlight404 } from '../lib/flight404Identity';

interface AuthContextType {
  user: UserProfile | null;
  isLoading: boolean;
  isSigningIn: boolean;
  error: string | null;
  clearError: () => void;
  signIn: () => Promise<void>;
  login: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<UserProfile>) => void;
  isAuthenticated: boolean;
  markAsExistingUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const POPUP_ATTEMPT_TIMEOUT_MS = 10 * 60 * 1_000;
const PROFILE_SYNC_RETRY_DELAYS_MS = [50, 150];
const PROFILE_NOT_PROVISIONED_MESSAGE = '此登入帳戶尚未完成遊戲個人資料設定，暫時無法進入遊戲。請使用既有遊戲帳戶登入或聯絡支援。';

class GameProfileNotProvisionedError extends Error {
  constructor() {
    super(PROFILE_NOT_PROVISIONED_MESSAGE);
    this.name = 'GameProfileNotProvisionedError';
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

function getLoginErrorMessage(error: unknown): string {
  if (error instanceof CentralSsoError) {
    const messages: Record<CentralSsoError['code'], string> = {
      central_sso_not_configured: '登入服務尚未設定，請稍後再試或聯絡支援。',
      central_sso_issuer_mismatch: '登入服務設定不一致，請聯絡支援。',
      central_sso_storage_failed: '瀏覽器無法安全儲存登入交易，請允許網站儲存後重試。',
      central_sso_state_mismatch: '登入回應驗證失敗，請關閉視窗後重新登入。',
      central_sso_missing_callback: '登入回應不完整，請重新登入。',
      central_sso_token_exchange_failed: '無法完成登入授權，請重新登入。',
      central_sso_invalid_token_response: '登入服務回應無效，請稍後再試。',
      central_sso_session_failed: '無法建立登入工作階段，請重新登入。',
      central_sso_popup_blocked: '瀏覽器封鎖登入彈窗，請允許彈窗後重試。',
      central_sso_popup_failed: '無法開啟登入彈窗，請重試。',
      central_sso_popup_closed: '登入彈窗已關閉，請重新登入。',
      central_sso_popup_timeout: '登入逾時，請關閉視窗後重新登入。',
      access_denied: '你已取消登入授權。',
      invalid_request: '登入請求無效，請重新登入。',
      invalid_client: '登入用戶端尚未設定，請聯絡支援。',
      invalid_redirect_uri: '登入回呼網址尚未設定，請聯絡支援。',
      invalid_grant: '登入授權已過期，請重新登入。',
      pkce_verification_failed: '登入安全驗證失敗，請重新登入。',
    };
    return messages[error.code];
  }
  if (error instanceof WorldWalletAuthError) {
    const messages: Record<WorldWalletAuthError['code'], string> = {
      world_app_unavailable: '請在 World App 內開啟後再登入。',
      world_app_not_configured: 'World App 登入尚未設定，請聯絡支援。',
      world_app_timeout: 'World App 登入逾時，請重試。',
      world_app_network_failed: '無法連線至登入服務，請檢查網路後重試。',
      world_app_nonce_failed: '無法建立安全登入請求，請重試。',
      world_app_rejected: '你已取消錢包授權。',
      world_app_wallet_missing: '無法取得錢包登入資料，請重試。',
      world_app_verification_failed: '錢包登入驗證失敗，請重試。',
      world_app_session_failed: '無法建立登入工作階段，請重試。',
      world_app_sign_in_failed: 'World App 登入失敗，請重試。',
    };
    return messages[error.code];
  }
  return error instanceof Error ? error.message : '登入失敗，請重試。';
}

function isWorldApp(): boolean {
  try {
    return MiniKit.isInstalled();
  } catch {
    return false;
  }
}

function isAuthSessionMissingError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const authError = error as { name?: unknown; code?: unknown };
  return authError.name === 'AuthSessionMissingError' || authError.code === 'session_not_found';
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useToast();
  const activePopupRef = useRef<Window | null>(null);
  const popupPollRef = useRef<number | null>(null);
  const popupTimeoutRef = useRef<number | null>(null);
  const popupMessageHandlerRef = useRef<((event: MessageEvent<unknown>) => void) | null>(null);
  const signInInFlightRef = useRef(false);
  const profileSyncEpochRef = useRef(0);
  const profileSyncPromiseRef = useRef<{
    sessionKey: string;
    promise: Promise<UserProfile | null>;
  } | null>(null);

  const clearPopupWait = useCallback(() => {
    if (popupPollRef.current !== null) {
      window.clearInterval(popupPollRef.current);
      popupPollRef.current = null;
    }
    if (popupTimeoutRef.current !== null) {
      window.clearTimeout(popupTimeoutRef.current);
      popupTimeoutRef.current = null;
    }
    if (popupMessageHandlerRef.current) {
      window.removeEventListener('message', popupMessageHandlerRef.current);
      popupMessageHandlerRef.current = null;
    }
    activePopupRef.current = null;
  }, []);

  const syncProfile = useCallback(async (
    sessionUserId?: string,
    sessionAccessToken?: string,
  ): Promise<UserProfile | null> => {
    const currentSession = (await supabase.auth.getSession()).data.session;
    const userId = sessionUserId ?? currentSession?.user.id;
    if (!userId) {
      setUser(null);
      return null;
    }

    const sessionKey = `${userId}:${sessionAccessToken ?? currentSession?.access_token ?? ''}`;
    const activeSync = profileSyncPromiseRef.current;
    if (activeSync?.sessionKey === sessionKey) {
      return activeSync.promise;
    }

    const syncEpoch = profileSyncEpochRef.current + 1;
    profileSyncEpochRef.current = syncEpoch;

    const syncPromise = (async () => {
      let profile: UserProfile | null = null;
      for (let attempt = 0; attempt <= PROFILE_SYNC_RETRY_DELAYS_MS.length; attempt += 1) {
        try {
          profile = await userAPI.getProfile();
          break;
        } catch (profileError) {
          if (!isAuthSessionMissingError(profileError) || attempt === PROFILE_SYNC_RETRY_DELAYS_MS.length) {
            throw profileError;
          }

          const { data: { session } } = await supabase.auth.getSession();
          if (!session || session.user.id !== userId) throw profileError;

          await new Promise<void>((resolve) => window.setTimeout(resolve, PROFILE_SYNC_RETRY_DELAYS_MS[attempt]));
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (
        profileSyncEpochRef.current !== syncEpoch
        || !session
        || session.user.id !== userId
        || session.access_token !== (sessionAccessToken ?? currentSession?.access_token ?? '')
      ) {
        return null;
      }

      if (!profile) {
        setUser(null);
        setError((currentError) => currentError ?? PROFILE_NOT_PROVISIONED_MESSAGE);
        return null;
      }

      setUser(profile);
      setError((currentError) => currentError === PROFILE_NOT_PROVISIONED_MESSAGE ? null : currentError);
      return profile;
    })();

    profileSyncPromiseRef.current = { sessionKey, promise: syncPromise };
    const clearSyncPromise = () => {
      if (profileSyncPromiseRef.current?.promise === syncPromise) {
        profileSyncPromiseRef.current = null;
      }
    };
    void syncPromise.then(clearSyncPromise, clearSyncPromise);
    return syncPromise;
  }, []);

  const refreshUserProfile = useCallback(async () => {
    try {
      await syncProfile();
    } catch (refreshError) {
      setUser(null);
      setError(getLoginErrorMessage(refreshError));
    }
  }, [syncProfile]);

  const syncFlight404Identity = useCallback(async (sessionUserId?: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session || (sessionUserId && session.user.id !== sessionUserId)) return;
    try {
      await bridgeSupabaseSessionToFlight404(session);
    } catch (bridgeError) {
      console.warn('[Bubble Game] Flight404 Firebase bridge unavailable');
      setError(getLoginErrorMessage(bridgeError));
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const applySession = (session: { user: { id: string } } | null) => {
      if (!session) {
        setUser(null);
        return;
      }
      void syncProfile(session.user.id, session.access_token)
        .then(async (profile) => {
          if (!profile) return;
          await syncFlight404Identity(session.user.id);
        })
        .catch((syncError) => {
          if (mounted) {
            setUser(null);
            setError(getLoginErrorMessage(syncError));
          }
        });
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => applySession(session));
    void (async () => {
      try {
        const callbackParams = new URLSearchParams(window.location.search);
        const isLegacyLinkCallback = callbackParams.get('mode') === 'link';
        if (!isLegacyLinkCallback) {
          await completeCentralSsoCallbackOnce(supabase);
        }
      } catch (callbackError) {
        if (mounted) setError(getLoginErrorMessage(callbackError));
      }
      try {
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        const profile = mounted
          ? await syncProfile(data.session?.user.id, data.session?.access_token)
          : null;
        if (data.session && profile) {
          await syncFlight404Identity(data.session.user.id);
        }
      } catch (bootstrapError) {
        if (mounted) {
          setUser(null);
          setError(getLoginErrorMessage(bootstrapError));
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearPopupWait();
    };
  }, [clearPopupWait, syncFlight404Identity, syncProfile]);

  const waitForCentralSsoPopup = useCallback(async () => new Promise<void>((resolve, reject) => {
    let completed = false;
    const finish = (callback?: () => void) => {
      if (completed) return;
      completed = true;
      clearPopupWait();
      callback?.();
    };

    let popupAttempt;
    try {
      popupAttempt = startCentralSsoPopup();
    } catch (popupError) {
      reject(popupError);
      return;
    }

    activePopupRef.current = popupAttempt.popup;
    const handleMessage = async (event: MessageEvent<unknown>) => {
      if (
        event.origin !== window.location.origin
        || event.source !== activePopupRef.current
        || !isCentralSsoPopupCallbackMessage(event.data)
      ) {
        return;
      }
      const popup = activePopupRef.current;
      finish();
      if (popup) acknowledgeCentralSsoPopupCallback(popup, event.data.callback.state ?? '');
      try {
        await completeCentralSsoPopupCallback(supabase, event.data);
        resolve();
      } catch (callbackError) {
        reject(callbackError);
      }
    };
    popupMessageHandlerRef.current = handleMessage;
    window.addEventListener('message', handleMessage);
    popupPollRef.current = window.setInterval(() => {
      if (!activePopupRef.current?.closed) return;
      finish();
      clearCentralSsoTransaction();
      reject(new CentralSsoError('central_sso_popup_closed'));
    }, 250);

    popupTimeoutRef.current = window.setTimeout(() => {
      const popup = activePopupRef.current;
      finish(() => {
        clearCentralSsoTransaction();
        popup?.close();
      });
      reject(new CentralSsoError('central_sso_popup_timeout'));
    }, POPUP_ATTEMPT_TIMEOUT_MS);

    popupAttempt.ready.catch((popupError) => {
      finish();
      clearCentralSsoTransaction();
      reject(popupError);
    });
  }), [clearPopupWait]);

  const signIn = useCallback(async () => {
    if (isLoading || signInInFlightRef.current) return;
    signInInFlightRef.current = true;
    setIsSigningIn(true);
    setError(null);
    try {
      if (isWorldApp()) {
        await signInWithWorldWallet();
      } else {
        await waitForCentralSsoPopup();
      }
      const profile = await syncProfile();
      if (!profile) {
        await supabase.auth.signOut({ scope: 'local' });
        throw new GameProfileNotProvisionedError();
      }
      await syncFlight404Identity();
    } catch (signInError) {
      const message = getLoginErrorMessage(signInError);
      setUser(null);
      setError(message);
      showError('登入失敗', message);
      throw signInError;
    } finally {
      signInInFlightRef.current = false;
      setIsSigningIn(false);
    }
  }, [isLoading, showError, syncFlight404Identity, syncProfile, waitForCentralSsoPopup]);

  const logout = useCallback(async () => {
    clearCentralSsoTransaction();
    clearPopupWait();
    try {
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) throw signOutError;
      await bridgeSupabaseSessionToFlight404(null);
      setUser(null);
      setError(null);
    } catch (logoutError) {
      const message = logoutError instanceof Error ? logoutError.message : '登出失敗，請重試。';
      showError('登出錯誤', message);
    }
  }, [clearPopupWait, showError]);

  const updateUser = useCallback((updates: Partial<UserProfile>) => {
    setUser((currentUser) => (currentUser ? { ...currentUser, ...updates } : currentUser));
  }, []);

  const markAsExistingUser = useCallback(async () => {
    const updatedUser = await userAPI.markAsExistingUser();
    setUser(updatedUser);
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    isSigningIn,
    error,
    clearError: () => setError(null),
    signIn,
    login: signIn,
    loginWithGoogle: signIn,
    refreshUserProfile,
    logout,
    updateUser,
    isAuthenticated: Boolean(user),
    markAsExistingUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
